import React, {
  useCallback,
  useState,
  ChangeEvent,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { Link, Outlet } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import api from '../../services/api';

import {
  ArchievementIcon,
  ArrowBackIcon,
  BellIcon,
  ChevronRightIcon,
  PencilIcon,
  TrashIcon,
  UserIdCardIcon,
} from '../../assets/svg/SVGComponents';

import { useAuth } from '../../hooks/auth';

import {
  MainContent,
  Dashboard,
  WelcomeSection,
  NameAndAvatarWrapper,
  AvatarInput,
  UserAvatar,
  LastAccess,
  ContentWrapper,
  MenuProfileContainer,
  MenuProfileInfo,
} from './styles';
import IRequestError from '../../dtos/IRequestError';
import { useToast } from '../../hooks/toast';
import avatar_api_letters from '../../services/avatarLetters';
import AddHardwareUnlockModal from '../../modals/AddHardwareUnlockModal';
import { useModal } from '../../hooks/modal';
import { useImageCompression } from '../../hooks/CompressionImage';
import CertificatesIcon from '../../assets/svg/SVGComponents/CertificatesIcon';
import LevelDisplayComponent from '../../components/LevelDisplayComponent';
import SectionTutorialButton from '../../components/Onboarding/SectionTutorialButton';
import { TutorialId } from '../../hooks/onboarding';

/**
 * Orcamento do avatar: 200 KB / 512px, o mesmo do IconUpload. O avatar so e
 * exibido em miniatura, entao nao ha ganho em subir a foto original da
 * camera do celular — e ela e a causa classica de 413 no upload.
 */
const AVATAR_MAX_BYTES = 200 * 1024;

interface IFormattedUserData {
  encodedName: string;
  last_access: string;
  firstName: string;
  lastName: string;
}

const Profile: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  const { t } = useTranslation();
  const { user, updateUser, isSessionExpired } = useAuth();
  const { addToast } = useToast();
  const { handleModal } = useModal();
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });

  const [isMobile, setIsMobile] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  // Sem argumentos de proposito: os defaults do hook ja sao
  // maxSizeMB 0.2 (200 KB) e maxWidthOrHeight 512, e passar um objeto
  // literal aqui trocaria a identidade de `compress` a cada render.
  const { compress, isCompressing } = useImageCompression();

  useEffect(() => {
    if (isTabletOrMobile || isPortrait) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [isPortrait, isTabletOrMobile]);

  const handleSelectedTabChange = useCallback((_event, tabIndex) => {
    setSelectedTab(tabIndex);
  }, []);

  const handleHardwareUnlockButton = useCallback(() => {
    handleModal(<AddHardwareUnlockModal />);
  }, [handleModal]);

  /**
   * As falhas de avatar mostravam `load_stats_fail` ("erro ao carregar as
   * estatísticas") nos três caminhos: era a mensagem errada e escondia a
   * causa, então formato recusado, arquivo grande demais e erro do servidor
   * eram indistinguíveis para o usuário e para o suporte.
   */
  const describeAvatarError = useCallback(
    (err: IRequestError): string => {
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.message;

      if (status === 413) {
        return t('toasts.errors.avatar_too_large', {
          defaultValue:
            'A imagem é muito grande. Escolha uma foto menor e tente de novo.',
        });
      }

      // O backend manda mensagens específicas (formato recusado, falha no
      // storage); a dele é sempre mais útil que uma genérica nossa.
      if (backendMessage) {
        return backendMessage;
      }

      return t('toasts.errors.avatar_upload_fail', {
        defaultValue:
          'Não foi possível atualizar sua foto de perfil. Tente novamente.',
      });
    },
    [t],
  );

  const handleAvatarChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || !e.target.files[0]) {
        return;
      }

      const file = e.target.files[0];

      // `accept="image/*"` não garante um formato utilizável: o iPhone
      // entrega HEIC e alguns navegadores mandam `type` vazio. Esse caminho
      // nem chega a fazer request — daí valer uma mensagem própria.
      if (!file.type.startsWith('image/')) {
        addToast({
          type: 'error',
          expression: 'Formato não suportado',
          description: t('toasts.errors.avatar_invalid_type', {
            defaultValue:
              'Selecione uma imagem JPG, PNG ou WEBP. Fotos em HEIC (iPhone) precisam ser convertidas antes.',
          }),
        });
        return;
      }

      let uploadFile = file;

      // Só re-encoda quem passa do orçamento: comprimir um avatar que já é
      // pequeno custaria qualidade sem economizar nada.
      if (file.size > AVATAR_MAX_BYTES) {
        try {
          const { file: compressed } = await compress(file);

          uploadFile = compressed;
        } catch (compressionError) {
          // A compressão é otimização, não requisito. O canvas não decodifica
          // todo formato (HEIC é o caso comum), e abortar aqui quebraria um
          // upload que o backend talvez aceitasse — então segue com o original.
          console.error(
            'Falha ao comprimir avatar, enviando original:',
            compressionError,
          );
        }
      }

      const data = new FormData();
      data.append('avatar', uploadFile);

      api
        .patch(`/users/avatar`, data)
        .then(response => {
          updateUser(response.data);
          if (avatarInputRef.current) {
            avatarInputRef.current.value = '';
          }
        })
        .catch(async (err: IRequestError) => {
          if (await isSessionExpired(err)) {
            return;
          }

          // Sem isto a causa real não sobra em lugar nenhum: o toast é a
          // única evidência que o usuário consegue reportar.
          console.error('Falha ao atualizar avatar:', err?.response ?? err);

          addToast({
            type: 'error',
            expression: 'Ops!',
            description: describeAvatarError(err),
          });
        });
    },
    [updateUser, t, isSessionExpired, addToast, describeAvatarError, compress],
  );

  const handleDeleteAvatar = useCallback(async () => {
    api
      .delete('/users/avatar')
      .then(response => {
        updateUser(response.data);
      })
      .catch(async (err: IRequestError) => {
        if (await isSessionExpired(err)) {
          return;
        }

        console.error('Falha ao remover avatar:', err?.response ?? err);

        addToast({
          type: 'error',
          expression: 'Ops!',
          description:
            err?.response?.data?.message ??
            t('toasts.errors.avatar_delete_fail', {
              defaultValue:
                'Não foi possível remover sua foto de perfil. Tente novamente.',
            }),
        });
      });
  }, [updateUser, addToast, isSessionExpired, t]);

  const formattedUserData = useMemo<IFormattedUserData>(() => {
    const encodedName = user.name.replace(' ', '+');
    const splittedName = user.name.split(' ');
    const firstName = splittedName[0];

    let lastName = '';

    if (splittedName.length > 1) {
      [, lastName] = splittedName;
    }

    const last_access = user.last_access
      ? format(parseISO(user.last_access), 'dd/MM/yyyy', {
          locale: ptBR,
        })
      : format(new Date(), 'dd/MM/yyyy', {
          locale: ptBR,
        });

    return {
      encodedName,
      last_access,
      firstName,
      lastName,
    };
  }, [user.name, user.last_access]);

  return (
    <MainContent className="profile-page">
      <Dashboard>
        {!isMobile && (
          <Link to={-1 as any}>
            <ArrowBackIcon
              width="1.33rem"
              stroke="#1E88E5"
              strokeWidth={3}
              fill="none"
            />
            {t('navlinks.back')}
          </Link>
        )}

        <WelcomeSection>
          <NameAndAvatarWrapper>
            <AvatarInput>
              <UserAvatar
                $avatarSrc={user.avatar_url || avatar_api_letters(user.name)}
              />
              {user.avatar_url && (
                <button type="button" onClick={() => handleDeleteAvatar()}>
                  <TrashIcon
                    width="1.33rem"
                    stroke="#1E88E5"
                    strokeWidth={2}
                    fill="none"
                  />
                </button>
              )}

              <label htmlFor="avatar">
                <PencilIcon
                  width="1.33rem"
                  stroke="#1E88E5"
                  strokeWidth={2}
                  fill="none"
                />

                <input
                  type="file"
                  id="avatar"
                  accept="image/*"
                  ref={avatarInputRef}
                  onChange={handleAvatarChange}
                  // Comprimir uma foto de câmera leva um instante: sem isto dá
                  // para escolher outro arquivo no meio e disparar dois uploads.
                  disabled={isCompressing}
                />
              </label>
            </AvatarInput>

            <span>
              {formattedUserData.lastName ? (
                <h1>
                  {formattedUserData.firstName}{' '}
                  <b>{formattedUserData.lastName}</b>
                </h1>
              ) : (
                <h1>
                  <b>{formattedUserData.firstName}</b>
                </h1>
              )}
            </span>
            <SectionTutorialButton tutorialId={TutorialId.PROFILE} />
          </NameAndAvatarWrapper>

          <LevelDisplayComponent
            displayMode="compact"
            current_points={user.total_points}
          />

          {!isMobile && (
            <LastAccess>
              {t('p.last_access')}: {formattedUserData.last_access}
            </LastAccess>
          )}
        </WelcomeSection>

        <ContentWrapper>
          <MenuProfileContainer>
            <MenuProfileInfo
              to="personal-data"
              id="menu-personal-data"
              className={({ isActive }) => (isActive ? 'active' : undefined)}
            >
              <UserIdCardIcon
                width="1rem"
                stroke="#323232"
                strokeWidth={1.5}
                fill="none"
              />
              {!isMobile && (
                <>
                  <span>Dados Pessoais</span>
                  <div>
                    <ChevronRightIcon
                      width="1rem"
                      stroke="#ff9800"
                      strokeWidth={2}
                    />
                  </div>
                </>
              )}
            </MenuProfileInfo>
            {/* archievements area */}
            <MenuProfileInfo
              to="achievements"
              id="menu-achievements"
              className={({ isActive }) => (isActive ? 'active' : undefined)}
            >
              <ArchievementIcon
                width="1rem"
                stroke="#323232"
                strokeWidth={1.5}
                fill="none"
              />
              {!isMobile && (
                <>
                  <span>Conquistas</span>
                  <div>
                    <ChevronRightIcon
                      width="1rem"
                      stroke="#ff9800"
                      strokeWidth={2}
                    />
                  </div>
                </>
              )}
            </MenuProfileInfo>
            {/* certificates area */}
            <MenuProfileInfo
              to="certificates"
              id="menu-certificates"
              className={({ isActive }) => (isActive ? 'active' : undefined)}
            >
              <CertificatesIcon
                width="1rem"
                stroke="#323232"
                strokeWidth={1.5}
                fill="none"
              />
              {!isMobile && (
                <>
                  <span>Certificados</span>
                  <div>
                    <ChevronRightIcon
                      width="1rem"
                      stroke="#ff9800"
                      strokeWidth={2}
                    />
                  </div>
                </>
              )}
            </MenuProfileInfo>
            {/* notifications area */}
            <MenuProfileInfo
              to="notifications"
              id="menu-notifications"
              className={({ isActive }) => (isActive ? 'active' : undefined)}
            >
              <BellIcon
                width="1.28rem"
                stroke="#323232"
                strokeWidth={1.5}
                fill="none"
              />
              {!isMobile && (
                <>
                  <span>Notificações</span>
                  <div>
                    <ChevronRightIcon
                      width="1rem"
                      stroke="#ff9800"
                      strokeWidth={2}
                    />
                  </div>
                </>
              )}
            </MenuProfileInfo>
          </MenuProfileContainer>
          <Outlet />
        </ContentWrapper>
      </Dashboard>
    </MainContent>
  );
};
export default Profile;
